/* eslint-disable react/forbid-prop-types */
/* eslint-disable react/require-default-props */
/* eslint-disable no-shadow */
/* eslint-disable react/no-unstable-nested-components */
import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Modal, Button, Form, Row, Col } from 'react-bootstrap';
import CreatableSelect from 'react-select/creatable';
import axios from '../../services/axios'; // Usar axios configurado com autenticação
import TagBadge from '../TagBadge';
import './TagSelector.css';

// Componente para exibir uma única tag selecionada
function CustomSingleValue({ data }) {
  return (
    <div className="tag-selector-single-value">
      <TagBadge tag={data.tag} size="small" />
    </div>
  );
}

CustomSingleValue.propTypes = {
  data: PropTypes.shape({
    tag: PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      name: PropTypes.string.isRequired,
      color: PropTypes.string,
    }).isRequired,
  }).isRequired,
};

// Componente para exibir múltiplas tags selecionadas
function CustomMultiValue({ data, removeProps }) {
  return (
    <div className="tag-selector-multi-value">
      <TagBadge tag={data.tag} size="small" />
      <button
        type="button"
        className="tag-selector-remove-btn"
        {...removeProps}
      >
        ×
      </button>
    </div>
  );
}

CustomMultiValue.propTypes = {
  data: PropTypes.shape({
    tag: PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      name: PropTypes.string.isRequired,
      color: PropTypes.string,
    }).isRequired,
  }).isRequired,
  removeProps: PropTypes.shape({
    onClick: PropTypes.func.isRequired,
  }).isRequired,
};

function TagSelector({
  selectedTags = [],
  onChange,
  isMulti = true,
  placeholder = 'Selecione as etiquetas...',
  isDisabled = false,
  size = 'medium',
  showSelectedBadges = true,
  required = false,
  error = null,
}) {
  const [availableTags, setAvailableTags] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newTagName, setNewTagName] = useState('');
  const [newTagColor, setNewTagColor] = useState('#007bff');
  const [newTagDescription, setNewTagDescription] = useState('');
  const [inputValue, setInputValue] = useState('');

  // Carregar etiquetas disponíveis
  useEffect(() => {
    const loadTags = async () => {
      try {
        setLoading(true);
        const response = await axios.get('/tags'); // URL corrigida sem /api
        setAvailableTags(response.data || []);
      } catch (loadError) {
        console.error('Erro ao carregar etiquetas:', loadError);
        setAvailableTags([]);
      } finally {
        setLoading(false);
      }
    };

    loadTags();
  }, []);

  // Função para criar nova etiqueta
  const createNewTag = async () => {
    if (!newTagName.trim()) return;

    try {
      setLoading(true);
      const response = await axios.post('/tags', {
        name: newTagName.trim(),
        color: newTagColor,
        description: newTagDescription.trim() || null,
      });

      const newTag = response.data;
      setAvailableTags((prev) => [...prev, newTag]);

      // Adicionar a nova tag às selecionadas
      const updatedSelectedTags = isMulti
        ? [...selectedTags, newTag]
        : [newTag];
      onChange(updatedSelectedTags);

      // Limpar formulário e fechar modal
      setNewTagName('');
      setNewTagColor('#007bff');
      setNewTagDescription('');
      setShowCreateModal(false);
      setInputValue('');
    } catch (createError) {
      console.error('Erro ao criar etiqueta:', createError);
      console.error('Erro ao criar etiqueta. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  // Função para editar etiqueta existente
  const editTag = async (tagId, updatedData) => {
    try {
      setLoading(true);
      const response = await axios.put(`/tags/${tagId}`, updatedData); // URL corrigida sem /api
      const updatedTag = response.data;

      // Atualizar a lista de tags disponíveis
      setAvailableTags((prev) =>
        prev.map((tag) => (tag.id === tagId ? updatedTag : tag))
      );

      // Atualizar as tags selecionadas se necessário
      const updatedSelectedTags = selectedTags.map((tag) =>
        tag.id === tagId ? updatedTag : tag
      );
      onChange(updatedSelectedTags);
    } catch (editError) {
      console.error('Erro ao editar etiqueta:', editError);
    } finally {
      setLoading(false);
    }
  };

  // Função para deletar etiqueta
  const deleteTag = async (tagId) => {
    try {
      setLoading(true);
      await axios.delete(`/tags/${tagId}`); // URL corrigida sem /api

      // Remover da lista de tags disponíveis
      setAvailableTags((prev) => prev.filter((tag) => tag.id !== tagId));

      // Remover das tags selecionadas se estiver presente
      const updatedSelectedTags = selectedTags.filter(
        (tag) => tag.id !== tagId
      );
      onChange(updatedSelectedTags);
    } catch (deleteError) {
      console.error('Erro ao deletar etiqueta:', deleteError);
    } finally {
      setLoading(false);
    }
  };

  // Transformar tags em opções para o Select
  const tagOptions = availableTags.map((tag) => ({
    value: tag.id,
    label: tag.name,
    tag,
  }));

  // Transformar tags selecionadas em valores para o Select
  const selectedValues = selectedTags.map((tag) => ({
    value: tag.id,
    label: tag.name,
    tag,
  }));

  // Função para lidar com mudanças na seleção
  const handleSelectChange = (selectedOptions) => {
    if (!selectedOptions) {
      onChange([]);
      return;
    }

    const tags = Array.isArray(selectedOptions)
      ? selectedOptions.map((option) => option.tag)
      : [selectedOptions.tag];

    onChange(tags);
  };

  // Função para lidar com criação de nova tag via input
  const handleCreateOption = (inputValue) => {
    setNewTagName(inputValue);
    setShowCreateModal(true);
  };

  // Função para verificar se uma opção pode ser criada
  const isValidNewOption = (inputValue) => {
    return (
      inputValue &&
      inputValue.trim().length > 0 &&
      !availableTags.some(
        (tag) => tag.name.toLowerCase() === inputValue.toLowerCase()
      )
    );
  };

  // Função para formatar a opção de criação
  const formatCreateLabel = (inputValue) => `Criar "${inputValue}"`;

  // Componente customizado para renderizar opções
  function CustomOption({ innerRef, innerProps, data, isSelected, isFocused }) {
    const optionClass = `tag-selector-option ${
      isSelected ? 'selected' : ''
    } ${isFocused ? 'focused' : ''}`;

    // Simplificar ternário aninhado
    let optionContent;
    if (data.isCreateOption) {
      optionContent = <span className="create-option">+ {data.label}</span>;
    } else {
      optionContent = (
        <div className="tag-option">
          <TagBadge tag={data.tag} size="small" />
          <span className="tag-name">{data.label}</span>
        </div>
      );
    }

    return (
      <div ref={innerRef} {...innerProps} className={optionClass}>
        {optionContent}
      </div>
    );
  }

  CustomOption.propTypes = {
    innerRef: PropTypes.oneOfType([
      PropTypes.func,
      PropTypes.shape({ current: PropTypes.instanceOf(Element) }),
    ]),
    innerProps: PropTypes.object,
    data: PropTypes.shape({
      isCreateOption: PropTypes.bool,
      label: PropTypes.string.isRequired,
      tag: PropTypes.shape({
        id: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
          .isRequired,
        name: PropTypes.string.isRequired,
        color: PropTypes.string,
      }),
    }).isRequired,
    isSelected: PropTypes.bool,
    isFocused: PropTypes.bool,
  };

  // Componente customizado para renderizar múltiplos valores
  function CustomMultiValueContainer({ data, removeProps }) {
    return (
      <div className="tag-selector-multi-value-container">
        <TagBadge tag={data.tag} size="small" />
        <button
          type="button"
          className="tag-selector-remove-btn"
          onClick={removeProps.onClick}
        >
          ×
        </button>
      </div>
    );
  }

  CustomMultiValueContainer.propTypes = {
    data: PropTypes.shape({
      tag: PropTypes.shape({
        id: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
          .isRequired,
        name: PropTypes.string.isRequired,
        color: PropTypes.string,
      }).isRequired,
    }).isRequired,
    removeProps: PropTypes.shape({
      onClick: PropTypes.func.isRequired,
    }).isRequired,
  };

  // Estilos customizados para o Select
  const getControlMinHeight = () => {
    if (size === 'small') return '32px';
    if (size === 'large') return '48px';
    return '40px';
  };

  const getControlBorderColor = (state) => {
    if (error) return '#dc3545';
    if (state.isFocused) return '#007bff';
    return '#ced4da';
  };

  const getControlBoxShadow = (state) => {
    if (!state.isFocused) return 'none';
    if (error) return '0 0 0 0.2rem rgba(220, 53, 69, 0.25)';
    return '0 0 0 0.2rem rgba(0, 123, 255, 0.25)';
  };

  const getOptionBackgroundColor = (state) => {
    if (state.isSelected) return '#007bff';
    if (state.isFocused) return '#f8f9fa';
    return 'white';
  };

  const customStyles = {
    control: (provided, state) => ({
      ...provided,
      minHeight: getControlMinHeight(),
      borderColor: getControlBorderColor(state),
      boxShadow: getControlBoxShadow(state),
      '&:hover': {
        borderColor: error ? '#dc3545' : '#007bff',
      },
    }),
    placeholder: (provided) => ({
      ...provided,
      color: '#6c757d',
      fontSize: size === 'small' ? '0.875rem' : '1rem',
    }),
    multiValue: () => ({
      display: 'none', // Esconder os valores padrão do react-select
    }),
    multiValueLabel: () => ({
      display: 'none',
    }),
    multiValueRemove: () => ({
      display: 'none',
    }),
    option: (provided, state) => ({
      ...provided,
      backgroundColor: getOptionBackgroundColor(state),
      color: state.isSelected ? 'white' : '#212529',
      padding: '8px 12px',
      cursor: 'pointer',
      '&:hover': {
        backgroundColor: state.isSelected ? '#0056b3' : '#e9ecef',
      },
    }),
    menu: (provided) => ({
      ...provided,
      zIndex: 9999,
    }),
    loadingIndicator: (provided) => ({
      ...provided,
      color: '#007bff',
    }),
  };

  return (
    <div className={`tag-selector ${size} ${error ? 'has-error' : ''}`}>
      {/* Select principal */}
      <CreatableSelect
        isMulti={isMulti}
        value={selectedValues}
        onChange={handleSelectChange}
        options={tagOptions}
        placeholder={placeholder}
        isDisabled={isDisabled || loading}
        isLoading={loading}
        isClearable
        isSearchable
        createOptionPosition="first"
        allowCreateWhileLoading={false}
        isValidNewOption={isValidNewOption}
        onCreateOption={handleCreateOption}
        formatCreateLabel={formatCreateLabel}
        inputValue={inputValue}
        onInputChange={setInputValue}
        components={{
          Option: CustomOption,
          SingleValue: CustomSingleValue,
          MultiValue: () => null, // Não renderizar os valores padrão
        }}
        styles={customStyles}
        className="tag-selector-select"
        classNamePrefix="tag-selector"
        noOptionsMessage={() => 'Nenhuma etiqueta encontrada'}
        loadingMessage={() => 'Carregando etiquetas...'}
      />

      {/* Exibir badges das tags selecionadas (apenas para modo múltiplo) */}
      {isMulti && showSelectedBadges && selectedTags.length > 0 && (
        <div className="tag-selector-selected-badges">
          {selectedTags.map((tag) => (
            <div key={tag.id} className="tag-selector-badge-container">
              <TagBadge tag={tag} size="small" />
              <button
                type="button"
                className="tag-selector-remove-badge-btn"
                onClick={() => {
                  const updatedTags = selectedTags.filter(
                    (t) => t.id !== tag.id
                  );
                  onChange(updatedTags);
                }}
                disabled={isDisabled}
              >
                ×
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Mensagem de erro */}
      {error && <div className="tag-selector-error">{error}</div>}

      {/* Indicador de campo obrigatório */}
      {required && <span className="tag-selector-required">*</span>}

      {/* Modal para criar nova etiqueta */}
      <Modal show={showCreateModal} onHide={() => setShowCreateModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Criar Nova Etiqueta</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Row>
              <Col md={8}>
                <Form.Group className="mb-3">
                  <Form.Label>Nome da Etiqueta</Form.Label>
                  <Form.Control
                    type="text"
                    value={newTagName}
                    onChange={(e) => setNewTagName(e.target.value)}
                    placeholder="Digite o nome da etiqueta"
                    maxLength={50}
                  />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Cor</Form.Label>
                  <Form.Control
                    type="color"
                    value={newTagColor}
                    onChange={(e) => setNewTagColor(e.target.value)}
                  />
                </Form.Group>
              </Col>
            </Row>
            <Form.Group className="mb-3">
              <Form.Label>Descrição (Opcional)</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                value={newTagDescription}
                onChange={(e) => setNewTagDescription(e.target.value)}
                placeholder="Digite uma descrição para a etiqueta"
                maxLength={255}
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setShowCreateModal(false)}
            disabled={loading}
          >
            Cancelar
          </Button>
          <Button
            variant="primary"
            onClick={createNewTag}
            disabled={loading || !newTagName.trim()}
          >
            {loading ? 'Criando...' : 'Criar Etiqueta'}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}

TagSelector.propTypes = {
  selectedTags: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      name: PropTypes.string.isRequired,
      color: PropTypes.string,
    })
  ),
  onChange: PropTypes.func.isRequired,
  isMulti: PropTypes.bool,
  placeholder: PropTypes.string,
  isDisabled: PropTypes.bool,
  size: PropTypes.oneOf(['small', 'medium', 'large']),
  showSelectedBadges: PropTypes.bool,
  required: PropTypes.bool,
  error: PropTypes.string,
};

TagSelector.defaultProps = {
  selectedTags: [],
  isMulti: true,
  placeholder: 'Selecione as etiquetas...',
  isDisabled: false,
  size: 'medium',
  showSelectedBadges: true,
  required: false,
  error: null,
};

export default TagSelector;
